// src/core/presets.ts
import { type DitherParams } from './types';

// Preset fields and their order matter for cross-version compatibility.
// Bump version if you change this.
const VERSION = 1;

type EncodableParams = Pick<DitherParams,
  | 'styleId' | 'scale' | 'tile' | 'paletteId' | 'invert'
  | 'contrast' | 'midtones' | 'highlights' | 'blur'
  | 'threshold' | 'thresholdEnabled'
>;

function pickEncodable(p: DitherParams): EncodableParams {
  return {
    styleId: p.styleId,
    scale: p.scale,
    tile: p.tile,
    paletteId: p.paletteId,
    invert: p.invert,
    contrast: p.contrast,
    midtones: p.midtones,
    highlights: p.highlights,
    blur: p.blur,
    threshold: p.threshold,
    thresholdEnabled: p.thresholdEnabled,
  };
}

export function encodePreset(p: DitherParams): string {
  const payload = { v: VERSION, ...pickEncodable(p) };
  const json = JSON.stringify(payload);
  // URL-safe base64
  const b64 = btoa(unescape(encodeURIComponent(json)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `dx1-${b64}`;
}

export function decodePreset(code: string): Partial<DitherParams> | null {
  const trimmed = code.trim();
  const m = /^dx1-([A-Za-z0-9_-]+)$/.exec(trimmed);
  if (!m) return null;
  try {
    const b64 = m[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(escape(atob(padded)));
    const obj = JSON.parse(json);
    if (typeof obj !== 'object' || obj === null || obj.v !== VERSION) return null;
    const out: Partial<DitherParams> = {};
    if (typeof obj.styleId === 'string') out.styleId = obj.styleId;
    if (typeof obj.scale === 'number') out.scale = obj.scale;
    if (typeof obj.tile === 'number') out.tile = obj.tile;
    if (typeof obj.paletteId === 'string') out.paletteId = obj.paletteId;
    if (typeof obj.invert === 'boolean') out.invert = obj.invert;
    if (typeof obj.contrast === 'number') out.contrast = obj.contrast;
    if (typeof obj.midtones === 'number') out.midtones = obj.midtones;
    if (typeof obj.highlights === 'number') out.highlights = obj.highlights;
    if (typeof obj.blur === 'number') out.blur = obj.blur;
    if (typeof obj.threshold === 'number') out.threshold = obj.threshold;
    if (typeof obj.thresholdEnabled === 'boolean') out.thresholdEnabled = obj.thresholdEnabled;
    return out;
  } catch {
    return null;
  }
}
